import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Close, DeleteOutline } from '@mui/icons-material';
import {
  useGetActiveQuarantinesQuery,
  useQuarantineDeviceMutation,
  useRemoveQuarantineMutation,
} from '../../../../services/SportVideoQueue/quarantine';
import { IVideoProcessingQueueDeviceOptionProps } from '../../../../services/SportVideoQueue/types';
import { COLORS } from './Constants';

const { tableHeaderColor, borderColorDarkTheme, fontColorDarkTheme } = COLORS;

interface QuarantineManagerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  devices: IVideoProcessingQueueDeviceOptionProps[];
}

const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString('pt-BR') : '-');

const inputSx = {
  '& .MuiInputBase-root': { color: fontColorDarkTheme, backgroundColor: tableHeaderColor },
  '& .MuiInputLabel-root': { color: 'gray' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: borderColorDarkTheme },
};

const QuarantineManagerDialog = ({ isOpen, onClose, devices }: QuarantineManagerDialogProps) => {
  const [deviceId, setDeviceId] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [durationMinutes, setDurationMinutes] = useState<string>('');

  const { data: quarantines, isLoading } = useGetActiveQuarantinesQuery();
  const { mutateAsync: quarantineDevice, isLoading: isSubmitting } = useQuarantineDeviceMutation();
  const { mutateAsync: removeQuarantine } = useRemoveQuarantineMutation();

  const quarantinedDeviceIds = new Set((quarantines ?? []).map((q) => q.deviceId));
  const availableDevices = devices.filter((d) => !quarantinedDeviceIds.has(d.deviceId));

  const handleAdd = async () => {
    if (!deviceId) return;
    await quarantineDevice({
      deviceId: Number(deviceId),
      reason: reason || undefined,
      durationMinutes: durationMinutes ? Number(durationMinutes) : undefined,
    });
    setDeviceId('');
    setReason('');
    setDurationMinutes('');
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Quarentena de dispositivos
        <IconButton onClick={onClose} size="small">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'gray', mb: 2 }}>
          Dispositivos em quarentena não entram mais no reinício automático de vídeos travados nem no
          coletor de erros — o pool de retry fica livre pros outros dispositivos.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <TextField
            select
            size="small"
            label="Dispositivo"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            sx={{ minWidth: 220, ...inputSx }}
          >
            {availableDevices.map((d) => (
              <MenuItem key={d.deviceId} value={d.deviceId}>
                {d.deviceName || `Dispositivo ${d.deviceId}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Motivo (opcional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ minWidth: 200, flex: 1, ...inputSx }}
          />
          <TextField
            size="small"
            type="number"
            label="Duração (min)"
            placeholder="Indefinida"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(e.target.value)}
            sx={{ width: 140, ...inputSx }}
          />
          <Button
            variant="contained"
            disabled={!deviceId || isSubmitting}
            onClick={handleAdd}
            sx={{ height: 40 }}
          >
            Colocar em quarentena
          </Button>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} sx={{ color: fontColorDarkTheme }} />
          </Box>
        ) : (quarantines ?? []).length === 0 ? (
          <Typography variant="body2" sx={{ color: 'gray', textAlign: 'center', py: 2 }}>
            Nenhum dispositivo em quarentena.
          </Typography>
        ) : (
          <List dense>
            {(quarantines ?? []).map((q) => (
              <ListItem
                key={q.id}
                sx={{ backgroundColor: tableHeaderColor, borderRadius: 1.5, mb: 1 }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => removeQuarantine(q.deviceId)}
                    title="Remover da quarentena"
                  >
                    <DeleteOutline fontSize="small" sx={{ color: '#ef5350' }} />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: fontColorDarkTheme, fontWeight: 600 }}>
                        {q.deviceName || `Dispositivo ${q.deviceId}`}
                      </Typography>
                      <Chip
                        size="small"
                        label={q.quarantinedUntil ? `até ${formatDateTime(q.quarantinedUntil)}` : 'indefinida'}
                        color={q.quarantinedUntil ? 'warning' : 'error'}
                        sx={{ borderRadius: 1 }}
                      />
                      <Chip
                        size="small"
                        label={q.isAutomatic ? 'Automática' : 'Manual'}
                        color={q.isAutomatic ? 'info' : 'default'}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: 'gray' }}>
                      {q.reason || 'Sem motivo informado'} — desde {formatDateTime(q.createdAt)}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: 'gray' }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuarantineManagerDialog;
